import { Key, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { addToast, Card, CardBody, Listbox, ListboxItem, Spinner } from "@heroui/react";
import { InfoIcon, FileTextIcon, ShieldIcon, FilesIcon, HeadsetIcon, ArrowsClockwiseIcon } from "@phosphor-icons/react";
import { useShallow } from "zustand/shallow";

import { EXTERNAL_LINKS, ICON_SIZE } from "@/config/constants";
import { useBoundStore } from "@/app/store";
import { storage } from "@/shared/utils/local-storage";
import { usePageLayout } from "@/app/hooks/usePageLayout";

export function HelpRoute() {
  const navigate = useNavigate();
  const [isSyncing, setIsSyncing] = useState(false);
  const userId = useBoundStore(useShallow((state) => state.user.id));
  const fetchNotes = useBoundStore(useShallow((state) => state.notes.fetchNotes));

  const handleAction = async (key: Key) => {
    switch (key) {
      case "contact":
        window.open(`${EXTERNAL_LINKS.contact}/?userId=${userId}`, "_blank", "noopener,noreferrer");
        break;
      case "about":
        window.open(EXTERNAL_LINKS.about, "_blank", "noopener,noreferrer");
        break;
      case "privacy":
        window.open(EXTERNAL_LINKS.privacy, "_blank", "noopener,noreferrer");
        break;
      case "terms":
        window.open(EXTERNAL_LINKS.terms, "_blank", "noopener,noreferrer");
        break;
      case "app-info":
        navigate("/settings/help/app-info");
        break;
      case "sync-data": {
        const lastForceSync = storage.getLastForceSync();
        const now = Date.now();
        const fiveMinutesMs = 5 * 60 * 1000;

        if (lastForceSync) {
          const lastSyncTime = parseInt(lastForceSync, 10);
          const timeSinceLastSync = now - lastSyncTime;

          if (timeSinceLastSync < fiveMinutesMs) {
            const remainingSeconds = Math.ceil((fiveMinutesMs - timeSinceLastSync) / 1000);
            const minutes = Math.floor(remainingSeconds / 60);
            const seconds = remainingSeconds % 60;
            addToast({
              color: "warning",
              title: "Sync in progress",
              description: `Please wait ${minutes}m ${seconds}s before syncing again.`,
            });
            return;
          }
        }

        setIsSyncing(true);
        try {
          storage.setLastForceSync(now.toString());
          await fetchNotes(true);
          addToast({
            color: "success",
            title: "Sync completed",
            description: "Your data has been successfully synchronized.",
          });
        } finally {
          setIsSyncing(false);
        }
        break;
      }
    }
  };

  const pageLayoutConfig = useMemo(() => ({ navbar: { title: "Help" } }), []);
  usePageLayout(pageLayoutConfig);

  return (
    <Card className="mx-auto max-w-lg">
      <CardBody>
        <Listbox aria-label="Help" onAction={handleAction}>
          <ListboxItem
            key="contact"
            startContent={<HeadsetIcon size={ICON_SIZE.lg} />}
            description="Get in touch with us for support or inquiries"
          >
            Contact us
          </ListboxItem>
          <ListboxItem
            key="about"
            startContent={<InfoIcon size={ICON_SIZE.lg} />}
            description="Learn about our app and mission"
          >
            About us
          </ListboxItem>
          <ListboxItem
            key="app-info"
            startContent={<FileTextIcon size={ICON_SIZE.lg} />}
            description="View application information"
          >
            App info
          </ListboxItem>
          <ListboxItem
            key="privacy"
            startContent={<ShieldIcon size={ICON_SIZE.lg} />}
            description="Read our privacy policy"
          >
            Privacy policy
          </ListboxItem>
          <ListboxItem
            key="terms"
            startContent={<FilesIcon size={ICON_SIZE.lg} />}
            description="Review terms and conditions"
          >
            Terms and Conditions
          </ListboxItem>
          <ListboxItem
            key="sync-data"
            isReadOnly={isSyncing}
            startContent={
              isSyncing ? <Spinner size="sm" color="current" /> : <ArrowsClockwiseIcon size={ICON_SIZE.lg} />
            }
            description="Use this if you think some of your data is missing"
          >
            Sync data
          </ListboxItem>
        </Listbox>
      </CardBody>
    </Card>
  );
}
